import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Card } from '@/components/ui/Card/Card';
import { Select } from '@/components/ui/Select/Select';
import { knowledgeService, type KnowledgeArticle } from '@/services/knowledgeService';
import { useNotificationStore } from '@/stores/notificationStore';

export function KnowledgeArticleEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotificationStore();
  const isNew = !id || id === 'new';

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [categories, setCategories] = useState<string[]>([]);
  const [chunkCount, setChunkCount] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isVectorizing, setIsVectorizing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(!isNew);

  useEffect(() => {
    knowledgeService.getCategories().then(setCategories).catch(() => {});
    if (!isNew) loadArticle();
  }, [id]);

  const loadArticle = async () => {
    try {
      const article = await knowledgeService.getArticle(id!);
      setTitle(article.title);
      setContent(article.content);
      setCategory(article.category || '');
      setStatus(article.status);
      setChunkCount(article.chunkCount ?? null);
    } catch (err) {
      notify.error('Failed to load article');
      navigate('/admin/knowledge');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (andVectorize = false) => {
    if (!title.trim() || !content.trim()) {
      notify.error('Title and content are required');
      return;
    }

    setIsSaving(true);
    try {
      let article: KnowledgeArticle;
      if (isNew) {
        article = await knowledgeService.createArticle({ title, content, category: category || undefined, status });
        notify.success('Article created');
        navigate(`/admin/knowledge/${article.id}`, { replace: true });
      } else {
        article = await knowledgeService.updateArticle(id!, { title, content, category: category || undefined, status });
        notify.success('Article saved');
      }

      if (andVectorize) {
        setIsVectorizing(true);
        try {
          const result = await knowledgeService.vectorize(article.id);
          setChunkCount(result.chunkCount);
          notify.success(`Vectorized: ${result.chunkCount} chunks created`);
        } catch (err: any) {
          notify.error(err.message || 'Failed to vectorize');
        } finally {
          setIsVectorizing(false);
        }
      }
    } catch (err: any) {
      notify.error(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this article? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await knowledgeService.deleteArticle(id!);
      notify.success('Article deleted');
      navigate('/admin/knowledge');
    } catch (err: any) {
      notify.error(err.message || 'Failed to delete');
      setIsDeleting(false);
    }
  };

  if (isLoading) return <PageContainer className="text-center"><span className="text-muted-foreground">Loading...</span></PageContainer>;

  return (
    <PageContainer>
      <PageHeader
        title={isNew ? 'New Article' : 'Edit Article'}
        description={isNew ? 'Create a new knowledge base article.' : 'Edit article content and settings.'}
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/knowledge')}>
            Back
          </Button>
          {!isNew && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
              isLoading={isDeleting}
            >
              Delete
            </Button>
          )}
        </div>
      </PageHeader>

      <div className="space-y-5 max-w-3xl">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Article title"
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Input
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Getting Started, Canvas, Billing"
              hint="Type a new category or pick an existing one"
            />
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {categories.map((cat) => (
                  <Badge
                    key={cat}
                    variant={category === cat ? 'default' : 'secondary'}
                    className="cursor-pointer"
                    onClick={() => setCategory(cat)}
                  >
                    {cat}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Select
            label="Status"
            value={status}
            onValueChange={(val) => setStatus(val as 'draft' | 'published')}
            options={[
              { value: 'draft', label: 'Draft' },
              { value: 'published', label: 'Published' },
            ]}
          />
        </div>

        <Textarea
          label="Content (Markdown)"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write your article content here. Markdown is supported."
          rows={16}
        />

        {/* Vectorization Status */}
        {!isNew && (
          <Card variant="default" padding="sm" className="text-sm text-muted-foreground flex items-center gap-2">
            {chunkCount !== null ? (
              <>
                <Badge variant="success">{chunkCount} chunks</Badge>
                <span>Indexed for AI search</span>
              </>
            ) : (
              <span>Not yet vectorized. Save and vectorize to make this article searchable by the AI.</span>
            )}
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => handleSave(false)}
            disabled={isSaving}
            isLoading={isSaving}
          >
            Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave(true)}
            disabled={isSaving || isVectorizing}
            isLoading={isVectorizing}
          >
            Save & Vectorize
          </Button>
        </div>
      </div>
    </PageContainer>
  );
}
